import { Component } from '@angular/core';
import { SortDirectionEnterprise } from '@lsq-frontend/angular-shared/api-service';
import { ModalService } from '@lsq-frontend/common-ui';
import {
    ListSupplierPaymentMethodsRequest,
    ListSupplierPaymentMethodsService,
    PaymentMethodStatus
} from '@lsq-frontend/remote-services/all-java/financing-mk1-services';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'toggle-supplier-eligibility-modal',
    styleUrls: ['./toggle-scf-enabled-modal.component.scss'],
    template: `
        <lsq-circle-loader *ngIf="loading$ | async"></lsq-circle-loader>
        <div *ngIf="!(loading$ | async)" class="modal-body toggle-supplier-eligibility-modal">
            <p class="bottom-margin body">{{ !scfEnabled ? ENABLED_BODY_1 : DISABLED_BODY_1 }}</p>
            <data-card>
                <div class="data-item">
                    <div class="data-header p label">Buyer:</div>
                    <div class="data-value p body">{{ buyerName }}</div>
                </div>
                <div class="data-item">
                    <div class="data-header p label">Supplier:</div>
                    <div class="data-value p body">{{ supplierPreferredName }}</div>
                </div>
            </data-card>
            <p class="bottom-margin body">{{ !scfEnabled ? ENABLED_BODY_2 : DISABLED_BODY_2 }}</p>
            <div class="warning" *ngIf="showBankRequired && !scfEnabled">
                <div class="control-row">
                    <div [style.width]="'100%'" [style.paddingRight]="'1rem'" class="p body">
                        A supplier must have a verified bank account before SCF can be enabled.
                    </div>
                </div>
            </div>
        </div>
    `
})
export class ToggleScfEnabledModalComponent {
    public DISABLED_BODY_1 =
        'You are disabling Supply Chain Finance for the following relationship:';
    public ENABLED_BODY_1 = 'You are enabling Supply Chain Finance for the following relationship:';
    public DISABLED_BODY_2 =
        'Once disabled, any invoices uploaded for this relationship will not appear in FastTrack and cannot be used for TPM, early payment, or FlexTerm.';
    public ENABLED_BODY_2 =
        'Once enabled, invoices uploaded for this relationship will appear in FastTrack and may be used for TPM, early payment, or FlexTerm.';

    public scfEnabled: boolean;
    public supplierPreferredName: string;
    public buyerName: string;
    public showBankRequired: boolean;
    public okButtonIsDisabled$?: BehaviorSubject<boolean>;
    public loading$ = new BehaviorSubject<boolean>(true);

    constructor(
        private _modalService: ModalService,
        private _listSupplierPaymentMethodsService: ListSupplierPaymentMethodsService
    ) {
        const modalData = this._modalService.modal.data;

        this.okButtonIsDisabled$ = modalData.okButtonIsDisabled$;
        this.scfEnabled = modalData.supplierRelationship.scfEnabled;
        this.supplierPreferredName = modalData.supplierRelationship.supplierPreferredName;
        this.buyerName = modalData.supplierRelationship.buyerPreferredName;
        if (!this.scfEnabled) {
            this.canEnabledScf(modalData.supplierRelationship.supplierKey);
        } else {
            this.okButtonIsDisabled$.next(false);
            this.loading$.next(false);
        }
    }

    public async canEnabledScf(supplierKey: string) {
        const accountVerified = await this.hasBankAccountVerified(supplierKey);
        this.showBankRequired = !accountVerified;
        this.okButtonIsDisabled$.next(this.showBankRequired);
        this.loading$.next(false);
    }

    private async hasBankAccountVerified(supplierKey: string): Promise<boolean> {
        const bankAccounts = await this._listSupplierPaymentMethodsService.executeOnce(
            new ListSupplierPaymentMethodsRequest({
                supplierKey: supplierKey,
                take: 100,
                skip: 0,
                sortColumn: 'default',
                sortDirection: SortDirectionEnterprise.Desc
            })
        );
        const bankAccount = bankAccounts?.rows[0];
        return (
            bankAccount?.default &&
            bankAccount?.paymentMethodStatus === PaymentMethodStatus.VERIFIED
        );
    }
}
